<template>
  <StyledSvgIcon :style="iconStyles" :class="className" v-html="svgContent" />
</template>

<script setup lang="ts">
import { getSvgContent } from '@/utils/svgAssets'
import { pxToRemValue } from '@/utils'
import { styled } from '@vue-styled-components/core'
import { computed, shallowRef, watch, type StyleValue } from 'vue'
import type { SvgIconProps } from '@/types/svg'

// 使用全局类型定义
const props = withDefaults(defineProps<SvgIconProps>(), {
  variant: 'default',
  hoverable: false,
  className: ''
})

// 基础 Styled Component
const StyledSvgIcon = styled.span`
  display: inline-block;
  vertical-align: middle;
  transition: transform 0.2s ease-in-out;

  &.hoverable {
    cursor: pointer;

    &:hover {
      transform: scale(1.1);
      opacity: 0.8;
    }
  }

  &.variant-outlined {
    border: 1px solid currentColor;
    border-radius: 2px;
    padding: 2px;
  }

  &.variant-filled {
    background-color: currentColor;
    border-radius: 2px;
    padding: 2px;

    svg {
      fill: white !important;
    }
  }

  &.variant-rounded {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    padding: 4px;
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`

// 计算样式
const iconStyles = computed(() => {
  const styles: StyleValue = {
    color: props.color
  }

  // 添加 rem 单位的辅助函数
  const addRemUnit = (value: string | number | undefined) => {
    return pxToRemValue(value, { rootValue: 100, minPixelValue: 2 })
  }

  // 如果没有设置任何尺寸，兼容之前的用外部class中font-size控制大小的写法
  if (!props.size && !props.width && !props.height) {
    styles.width = '1em'
    styles.height = '1em'
    return styles
  }

  // 优先使用独立的 width 和 height 属性
  if (props.width !== undefined || props.height !== undefined) {
    styles.width = addRemUnit(props.width)
    styles.height = addRemUnit(props.height)
  } else if (props.size) {
    // 处理 size 属性
    if (props.size === 'auto') {
      // 自适应尺寸
      styles.width = 'auto'
      styles.height = 'auto'
    } else if (typeof props.size === 'string' && props.size.includes(' ')) {
      const [width, height] = props.size.split(' ')
      styles.width = addRemUnit(width)
      styles.height = addRemUnit(height)
    } else {
      // 单个值，宽高相等
      const sizeValue = addRemUnit(props.size)
      styles.width = sizeValue
      styles.height = sizeValue
    }
  } else {
    // 如果没有任何尺寸设置，使用默认大小
    styles.width = '24px'
    styles.height = '24px'
  }

  return styles
})

// 计算类名
const className = computed(() => {
  const classes = [props.className]

  if (props.hoverable) {
    classes.push('hoverable')
  }

  if (props.variant !== 'default') {
    classes.push(`variant-${props.variant}`)
  }

  return classes.filter(Boolean).join(' ')
})

const svgContent = shallowRef('')

watch(
  () => props.name,
  async (name, _oldName, onCleanup) => {
    let cancelled = false
    onCleanup(() => {
      cancelled = true
    })

    svgContent.value = ''
    const content = await getSvgContent(name)
    if (!cancelled) svgContent.value = content
  },
  { immediate: true }
)
</script>
